/**
 * SearchInput — input avec icone loupe, debounce 300ms, bouton clear.
 *
 *   <SearchInput value={q} onChange={setQ} placeholder="Rechercher une œuvre…" />
 *
 * Composant **fully controlled**. Le parent owne la state `value`, on lui
 * remonte chaque frappe via `onChange` (instant) ; si `onSearch` est fourni,
 * il est appele apres `debounceMs` (default 300ms) pour declencher l'appel
 * API ou la mise a jour de l'URL canonique. Pattern naturel pour
 * `useListFilters` (S2.4).
 */
import { useEffect, useRef, type ChangeEvent } from 'react'
import { Search, X } from 'lucide-react'

import './SearchInput.css'

interface SearchInputProps {
  value: string
  onChange: (value: string) => void
  /** Callback debounce (default 300ms). Si non fourni, seul `onChange` est appele. */
  onSearch?: (value: string) => void
  placeholder?: string
  debounceMs?: number
  ariaLabel?: string
  className?: string
}

export default function SearchInput({
  value,
  onChange,
  onSearch,
  placeholder = 'Rechercher…',
  debounceMs = 300,
  ariaLabel = 'Recherche',
  className,
}: SearchInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!onSearch) return
    const t = setTimeout(() => onSearch(value), debounceMs)
    return () => clearTimeout(t)
  }, [value, debounceMs, onSearch])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
  }

  const handleClear = () => {
    onChange('')
    onSearch?.('')
    inputRef.current?.focus()
  }

  const classes = ['search-input', className].filter(Boolean).join(' ')

  return (
    <div className={classes}>
      <Search size={16} className="search-input-icon" aria-hidden="true" />
      <input
        ref={inputRef}
        type="search"
        className="search-input-input"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        aria-label={ariaLabel}
      />
      {value && (
        <button
          type="button"
          className="search-input-clear"
          onClick={handleClear}
          aria-label="Effacer la recherche"
        >
          <X size={14} aria-hidden="true" />
        </button>
      )}
    </div>
  )
}
